import { useCallback, useEffect, useRef } from 'react';

interface UseTablePaginationParams {
  paginationMode: 'pagination' | 'clientSide' | 'loadMore' | 'infiniteScroll' | 'none';
  pageSize: number;
  stateActions: {
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
  };
  setTableInfo?: (info: Record<string, unknown>) => void;
  tableInfo?: Record<string, unknown>;
  onLoadMore?: () => void;
  loadMoreStatus?: 'idle' | 'loading' | 'done' | 'success';
  externalDataProvided: boolean;
  remoteHasMore: boolean;
  remoteLoadMore?: () => void;
  remoteIsLoadingMore: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Changes when search/filters/sort change — resets table body scroll to top (infiniteScroll). */
  queryScrollResetKey?: string;
}

interface UseTablePaginationReturn {
  handlePageChange: (pageIndex: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
}

export function useTablePagination({
  paginationMode,
  pageSize,
  stateActions,
  setTableInfo,
  tableInfo,
  onLoadMore,
  loadMoreStatus,
  externalDataProvided,
  remoteHasMore,
  remoteLoadMore,
  remoteIsLoadingMore,
  scrollContainerRef,
  queryScrollResetKey,
}: UseTablePaginationParams): UseTablePaginationReturn {
  const lastQueryScrollResetKeyRef = useRef<string | undefined>(undefined);

  // Reset scroll position when the remote query changes so infinite scroll can reload from the top.
  useEffect(() => {
    if (paginationMode !== 'infiniteScroll') return;
    const key = queryScrollResetKey ?? '';
    if (lastQueryScrollResetKeyRef.current === undefined) {
      lastQueryScrollResetKeyRef.current = key;
      return;
    }
    if (lastQueryScrollResetKeyRef.current === key) return;
    lastQueryScrollResetKeyRef.current = key;
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [paginationMode, queryScrollResetKey, scrollContainerRef]);

  const tryLoadMoreNearBottom = useCallback(
    (container: HTMLDivElement, loadMore: () => void, blockWhileLoading: boolean) => {
      if (blockWhileLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    },
    [],
  );

  const handlePageChange = useCallback(
    (pageIndex: number) => {
      stateActions.setPage(pageIndex);
      if (setTableInfo && tableInfo) {
        setTableInfo({
          ...tableInfo,
          skipCount: pageIndex * pageSize,
        });
      }
    },
    [stateActions, setTableInfo, tableInfo, pageSize]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      stateActions.setPageSize(newPageSize);
      if (setTableInfo && tableInfo) {
        setTableInfo({ ...tableInfo, skipCount: 0, maxResultCount: newPageSize });
      }
    },
    [stateActions, setTableInfo, tableInfo]
  );

  // After new rows append, re-check if we're still near the bottom to auto-trigger the next page.
  const prevLoadingMoreRef = useRef(false);
  useEffect(() => {
    if (paginationMode !== 'infiniteScroll') return;
    const wasLoading = prevLoadingMoreRef.current;
    prevLoadingMoreRef.current = remoteIsLoadingMore;

    if (wasLoading && !remoteIsLoadingMore && remoteHasMore) {
      const container = scrollContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        remoteLoadMore?.();
      }
    }
  }, [paginationMode, remoteIsLoadingMore, remoteHasMore, remoteLoadMore, scrollContainerRef]);

  // Infinite scroll listener
  useEffect(() => {
    if (paginationMode !== 'infiniteScroll') return;

    const effectiveOnLoadMore = onLoadMore ?? remoteLoadMore;
    if (!effectiveOnLoadMore) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isCurrentlyLoadingMore = loadMoreStatus === 'loading' || remoteIsLoadingMore;
      if (!onLoadMore && !externalDataProvided && !remoteHasMore) return;
      tryLoadMoreNearBottom(container, effectiveOnLoadMore, isCurrentlyLoadingMore);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    paginationMode,
    onLoadMore,
    loadMoreStatus,
    externalDataProvided,
    remoteHasMore,
    remoteLoadMore,
    remoteIsLoadingMore,
    scrollContainerRef,
    tryLoadMoreNearBottom,
  ]);

  return {
    handlePageChange,
    handlePageSizeChange,
  };
}
